// Interface themes ("skins"): a second axis beside light/dark. The active
// skin is set as data-skin on <html>; each skin is one CSS file under
// styles/skins/ scoped to [data-skin="<id>"] (token overrides first, then
// component overrides). Adding a skin = an entry here + its CSS file + one
// @import in index.css. index.html repeats the read logic inline so the
// first paint already carries the right skin.

export type SkinId = "instrument" | "polar";

export interface SkinDefinition {
  id: SkinId;
  /** Name shown in the pickers. */
  name: string;
  /** Dark-only skins force dark tokens and disable the light/dark toggle. */
  darkOnly: boolean;
  /** Google Fonts stylesheet loaded only while this skin is active. */
  fontsHref?: string;
  /** Two colours for the picker swatch: surface, signal. */
  swatch: [string, string];
}

export const SKINS: readonly SkinDefinition[] = [
  {
    id: "instrument",
    name: "Instrument",
    darkOnly: true,
    fontsHref:
      "https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,100..900&family=Martian+Mono:wdth,wght@75..112.5,100..800&display=swap",
    swatch: ["#151719", "#ffa51f"],
  },
  {
    id: "polar",
    name: "Polar Night",
    darkOnly: false,
    // Polar's fonts (Geist, Geist Mono, Instrument Serif) load from index.html.
    swatch: ["#0a1620", "#5ee7ff"],
  },
];

export const DEFAULT_SKIN: SkinId = "instrument";
export const SKIN_STORAGE_KEY = "nexusDialerSkin";
export const SKIN_URL_PARAM = "skin";
const FONT_LINK_PREFIX = "skin-fonts-";

export const isSkinId = (value: unknown): value is SkinId =>
  typeof value === "string" && SKINS.some((skin) => skin.id === value);

export const getSkin = (id: SkinId): SkinDefinition =>
  SKINS.find((skin) => skin.id === id) ?? SKINS[0];

export const skinLabel = (id: SkinId): string =>
  id === DEFAULT_SKIN ? `${getSkin(id).name} (default)` : getSkin(id).name;

const readStoredSkin = (): SkinId | null => {
  try {
    const stored = window.localStorage.getItem(SKIN_STORAGE_KEY);
    return isSkinId(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const persistSkin = (id: SkinId): void => {
  try {
    window.localStorage.setItem(SKIN_STORAGE_KEY, id);
  } catch {
    // Storage blocked (private mode, quota): the choice lasts this page only.
  }
};

/**
 * The skin to start with: a valid ?skin= URL param (persisted, for sharing a
 * link), then the saved choice, then the default.
 */
export const readInitialSkin = (): SkinId => {
  if (typeof window === "undefined") return DEFAULT_SKIN;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(SKIN_URL_PARAM);
    if (isSkinId(fromUrl)) {
      persistSkin(fromUrl);
      return fromUrl;
    }
  } catch {
    // Malformed location: fall through to storage.
  }
  return readStoredSkin() ?? DEFAULT_SKIN;
};

/** Inject the skin's font stylesheet once; skins without one add nothing. */
export const ensureSkinFonts = (id: SkinId): void => {
  if (typeof document === "undefined") return;
  const href = getSkin(id).fontsHref;
  if (!href) return;
  const linkId = `${FONT_LINK_PREFIX}${id}`;
  if (document.getElementById(linkId)) return;
  const link = document.createElement("link");
  link.id = linkId;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

export const applySkinToRoot = (root: HTMLElement, id: SkinId): void => {
  root.setAttribute("data-skin", id);
};
